import { type ReactNode } from 'react'
import { confirmDelete, useRequiredParams } from '@util'
import {
  UnsavedCompliancePolicySchema,
  type CompliancePolicy,
  type CompliancePolicyId,
  type UnsavedCompliancePolicy,
} from '@terros/common'
import { useNavigate, useRouter, createFileRoute } from '@tanstack/react-router'
import { notifications, showNotification } from '@mantine/notifications'
import { useForm } from '@mantine/form'
import { useToastError } from '@hooks'
import {
  useCompliancePolicy,
  useRemoveCompliancePolicy,
  useTranslation,
  useUpdateCompliancePolicy,
  zodResolver,
} from '@front/model'
import { CompliancePolicyForm, ButtonDelete, ButtonExternal, ButtonSave, DangerZone, NotReady, Page } from '@components'

type Params = {
  policyId: CompliancePolicyId
}

function EditCompliancePolicy(): ReactNode {
  const { policyId } = useRequiredParams<Params>()

  const { data: policy, isLoading, error } = useCompliancePolicy(policyId)

  if (isLoading || error || !policy) {
    return <NotReady data={policy} error={error} id='Policy' isLoading={isLoading} />
  }

  return <EditPolicy policy={policy} />
}

const EditPolicy = ({ policy }: { policy: CompliancePolicy }): ReactNode => {
  const navigate = useNavigate()
  const router = useRouter()
  const { t } = useTranslation()
  const { policyId } = policy

  const { update, isUpdating, error: updateError } = useUpdateCompliancePolicy()
  const { remove, isRemoving, error: removeError } = useRemoveCompliancePolicy()
  useToastError(updateError)
  useToastError(removeError)

  const form = useForm<UnsavedCompliancePolicy>({
    initialValues: UnsavedCompliancePolicySchema.parse(policy),
    validate: zodResolver(UnsavedCompliancePolicySchema),
  })
  const { isDeleted } = form.getValues()

  const handleSubmit = async (policy: UnsavedCompliancePolicy): Promise<void> => {
    await update({ policy: { ...policy, policyId } })
    router.history.back()
    form.resetDirty(policy)
    notifications.show({ message: 'Compliance Policy Saved', color: 'green' })
  }

  const handleRemove = async (archive: boolean): Promise<void> => {
    const confirmed = await confirmDelete({
      title: `${archive ? 'Archive' : 'Delete'} Policy`,
      message: `Are you sure you want to ${archive ? 'archive' : 'delete'} this compliance policy?`,
      label: archive ? 'Archive' : 'Delete',
      t,
    })
    if (!confirmed) return

    await remove({ policyId, archive })
    showNotification({
      message: `Policy ${archive ? 'Archived' : 'Deleted'}`,
      color: 'blue',
    })
    if (archive) {
      form.setFieldValue('isDeleted', true)
      form.resetDirty()
    } else {
      router.history.back()
    }
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Page>
        <Page.Heading title='Edit Compliance Policy' back>
          <ButtonExternal to={`/compliance/violations/${policyId}`} variant='ghost'>
            View Violations
          </ButtonExternal>
          <ButtonSave type='submit' loading={isUpdating} disabled={!form.isValid() || !form.isDirty()} />
        </Page.Heading>
        <Page.Content scrollArea>
          <CompliancePolicyForm form={form} />
          <DangerZone>
            <ButtonDelete loading={isRemoving} onClick={() => handleRemove(!isDeleted)}>
              {isDeleted ? 'Delete' : 'Archive'}
            </ButtonDelete>
          </DangerZone>
        </Page.Content>
      </Page>
    </form>
  )
}

export const Route = createFileRoute('/compliance_/policy_/$policyId/')({
  component: EditCompliancePolicy,
})
